import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import sigmaLogo from '../Images/sigma-bank-logo.png';

/**
 * Shown instead of the app when an account has been banned or blocked by an
 * admin. Rendered by ProtectedRoute, which only reaches here once the profile
 * has loaded and the status is explicitly suspended.
 *
 * The user keeps a valid session, so we give them an explicit sign-out rather
 * than trapping them on a dead screen.
 */
const SuspendedBlocker = ({ status }) => {
    const { signOut } = useAuth();
    const isBlocked = String(status || '').toLowerCase() === 'blocked';

    const handleSignOut = async () => {
        try {
            await signOut();
        } finally {
            window.location.href = '/sign-in';
        }
    };

    return (
        <div style={styles.wrapper}>
            <div style={styles.card}>
                <div style={styles.logoCircle}>
                    <img src={sigmaLogo} alt="Sigma Bank" style={styles.logoImage} />
                </div>

                <div style={styles.iconRing}>
                    <i className="bx bx-lock-alt" style={{ fontSize: 34, color: '#fca5a5' }} />
                </div>

                <h1 style={styles.title}>
                    {isBlocked ? 'Account Temporarily Blocked' : 'Account Suspended'}
                </h1>
                <p style={styles.message}>
                    {isBlocked
                        ? 'Your Sigma Bank account has been temporarily blocked, so you can’t access it right now.'
                        : 'Your Sigma Bank account has been suspended and you no longer have access to it.'}
                </p>

                <div style={styles.divider} />

                <p style={styles.help}>
                    If you believe this is a mistake, contact our support team and we’ll look into it.
                </p>
                <a href="mailto:support@sigmabank.ai" style={styles.supportLink}>
                    <i className="bx bx-envelope" />
                    support@sigmabank.ai
                </a>

                <button onClick={handleSignOut} style={styles.signOutBtn}>
                    <i className="bx bx-log-out" />
                    Sign Out
                </button>
            </div>
            <p style={styles.copyright}>&copy; {new Date().getFullYear()} Sigma Bank. All rights reserved.</p>
        </div>
    );
};

const styles = {
    wrapper: {
        minHeight: '100vh',
        width: '100%',
        background: 'linear-gradient(160deg, #060320 0%, #0e0758 30%, #1a0d4d 60%, #0a0630 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '32px 20px',
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    },
    card: {
        width: '100%',
        maxWidth: 420,
        background: 'rgba(255,255,255,0.05)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 24,
        padding: '40px 28px 32px',
        textAlign: 'center',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    },
    logoCircle: {
        width: 72,
        height: 72,
        borderRadius: '50%',
        background: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 24px',
    },
    logoImage: { width: 44, height: 'auto' },
    iconRing: {
        width: 72,
        height: 72,
        borderRadius: '50%',
        background: 'rgba(239,68,68,0.12)',
        border: '1px solid rgba(239,68,68,0.25)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 20px',
    },
    title: {
        color: '#fff',
        fontSize: 22,
        fontWeight: 700,
        margin: '0 0 12px',
    },
    message: {
        color: 'rgba(255,255,255,0.65)',
        fontSize: 14,
        lineHeight: 1.6,
        margin: 0,
    },
    divider: {
        width: 48,
        height: 2,
        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.25), transparent)',
        margin: '26px auto',
    },
    help: {
        color: 'rgba(255,255,255,0.5)',
        fontSize: 13,
        lineHeight: 1.6,
        margin: '0 0 12px',
    },
    supportLink: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        color: '#19bfb1',
        fontSize: 14,
        fontWeight: 600,
        textDecoration: 'none',
        marginBottom: 28,
    },
    signOutBtn: {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        padding: '14px 20px',
        marginTop: 8,
        background: 'rgba(255,255,255,0.1)',
        border: '1px solid rgba(255,255,255,0.18)',
        borderRadius: 12,
        color: '#fff',
        fontSize: 15,
        fontWeight: 600,
        cursor: 'pointer',
    },
    copyright: {
        color: 'rgba(255,255,255,0.25)',
        fontSize: 11,
        marginTop: 24,
    },
};

export default SuspendedBlocker;
